/* eslint-disable no-restricted-syntax */
import _ from 'lodash';

const MAX_WIDTH_CACHE_SIZE = 5000;
const FONT_TEXT_WIDTH_CACHE = new WeakMap();

/**
 * Resolves the memo table holding measured text widths for a specific PDF font and font size. Returns null for a missing font or nil size, otherwise walks the two-level FONT_TEXT_WIDTH_CACHE map, creating the per-font and per-size maps on first use.
 *
 * @param font - PDF font object used as the outer cache key.
 * @param fontSize - Font size used as the inner cache key.
 * @returns The width map for that font and size, or null.
 */
const getFontWidthCache = (font: any, fontSize: any) => {
  if (!font || _.isNil(fontSize)) {
    return null;
  }

  let sizeMap = FONT_TEXT_WIDTH_CACHE.get(font);
  if (!sizeMap) {
    sizeMap = new Map();
    FONT_TEXT_WIDTH_CACHE.set(font, sizeMap);
  }

  let textWidthMap = sizeMap.get(fontSize);
  if (!textWidthMap) {
    textWidthMap = new Map();
    sizeMap.set(fontSize, textWidthMap);
  }

  return textWidthMap;
};

/**
 * Measures the rendered width of report text, reusing previously measured values. Looks the string up in the per-font, per-size width cache, otherwise calls the font's widthOfTextAtSize, clears the cache once it reaches MAX_WIDTH_CACHE_SIZE, and stores the new width.
 *
 * @param font - PDF font used for measurement.
 * @param fontSize - Font size in points.
 * @param value - Text to measure, coerced to a string.
 * @returns The measured text width.
 */
const measureTextWidth = (font: any, fontSize: any, value = '') => {
  const textToMeasure = String(value);
  const widthCache = getFontWidthCache(font, fontSize);

  if (widthCache && widthCache.has(textToMeasure)) {
    return widthCache.get(textToMeasure);
  }

  const width = font.widthOfTextAtSize(textToMeasure, fontSize);

  if (widthCache) {
    if (widthCache.size >= MAX_WIDTH_CACHE_SIZE) {
      widthCache.clear();
    }
    widthCache.set(textToMeasure, width);
  }

  return width;
};

/**
 * Wraps report text into at most maxLines lines that fit a column width, ellipsizing any overflow.
 * Splits on whitespace, breaks over-wide words into fitting chunks, greedily fills lines by measured
 * width, and when lines exceed maxLines keeps the first ones with the last trimmed to fit an ellipsis.
 *
 * @param props - Options carrying text, font, fontSize, maxWidth and maxLines.
 * @returns An object carrying the wrapped lines and their rowCount.
 */
const truncateTextAndRowCount = ({ text, font, fontSize, maxWidth, maxLines = 2 }: any) => {
  const ellipsis = '...';
  const safeText = String(text || '').trim();

  if (!safeText || maxLines <= 0 || maxWidth <= 0) {
    return { lines: [''], rowCount: 1 };
  }

  /**
   * Measures a string's width using the enclosing draw call's font and font size. Delegates to the cached measureTextWidth with those bound values.
   *
   * @param value - Text to measure.
   * @returns The measured text width.
   */
  const measureWidth = (value = '') => measureTextWidth(font, fontSize, value);

  /**
   * Finds the longest leading substring of a value that still fits inside a pixel width budget. Returns an empty string for missing input or a non-positive limit and the whole value when it already fits, otherwise binary searches prefix lengths using the cached width measurement.
   *
   * @param value - Text to truncate.
   * @param widthLimit - Maximum allowed rendered width.
   * @returns The longest prefix fitting the width limit.
   */
  const fitPrefixByWidth = (value: any, widthLimit: any) => {
    if (!value || widthLimit <= 0) {
      return '';
    }

    if (measureWidth(value) <= widthLimit) {
      return value;
    }

    let low = 0;
    let high = value.length;

    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      const prefix = value.slice(0, mid);

      if (measureWidth(prefix) <= widthLimit) {
        low = mid;
      } else {
        high = mid - 1;
      }
    }

    return value.slice(0, low);
  };

  /**
   * Breaks a single word that is too wide for the report column into width-bounded chunks. Repeatedly takes the longest fitting prefix within maxWidth, falling back to one character when nothing fits, and consumes the word until it is exhausted.
   *
   * @param word - Word to split.
   * @returns The word split into chunks that each fit the maximum width.
   */
  const splitLongWord = (word: any) => {
    const chunks = [];
    let remaining = word;

    while (remaining) {
      let chunk = fitPrefixByWidth(remaining, maxWidth);

      if (!chunk) {
        chunk = remaining[0];
      }

      chunks.push(chunk);
      remaining = remaining.slice(chunk.length);
    }

    return chunks;
  };

  const tokens = safeText.split(/\s+/);
  const wrappedLines = [];
  let currentLine = '';

  for (const token of tokens) {
    const tokenChunks = measureWidth(token) <= maxWidth ? [token] : splitLongWord(token);

    for (const chunk of tokenChunks) {
      const candidate = currentLine ? `${currentLine} ${chunk}` : chunk;

      if (measureWidth(candidate) <= maxWidth) {
        currentLine = candidate;
      } else {
        if (currentLine) {
          wrappedLines.push(currentLine);
        }
        currentLine = chunk;
      }
    }
  }

  if (currentLine) {
    wrappedLines.push(currentLine);
  }

  if (wrappedLines.length <= maxLines) {
    return { lines: wrappedLines, rowCount: wrappedLines.length };
  }

  const lines = wrappedLines.slice(0, maxLines);
  const lastIndex = maxLines - 1;
  const ellipsisWidth = measureWidth(ellipsis);
  const availableWidth = Math.max(maxWidth - ellipsisWidth, 0);

  lines[lastIndex] = `${fitPrefixByWidth(lines[lastIndex], availableWidth)}${ellipsis}`;
  return { lines, rowCount: lines.length };
};

export { measureTextWidth, truncateTextAndRowCount };
